/** Build a recipe from ingredients. Totals update as you go; with a cooked
 *  weight, portions can be weighed straight out of the pot. */

import { useState } from 'react'

import type { RecipeRow } from '../db'
import { deleteRecipe, saveRecipe } from '../db/mutations'
import { refParts, type FoodItem } from '../lib/foodItem'
import { fmt, fmtKcal } from '../lib/nutrients'
import { recipeTotals, type Ingredient } from '../lib/recipe'
import { Search, useOnline } from './AddFood'
import { FoodPortion } from './FoodPortion'

export function RecipeEditor({ recipe, onDone }: { recipe: RecipeRow | null; onDone: () => void }) {
  const online = useOnline()
  const [name, setName] = useState(recipe?.name ?? '')
  const [servings, setServings] = useState(String(recipe?.servings ?? 4))
  const [cooked, setCooked] = useState(recipe?.cooked_weight_g ? String(recipe.cooked_weight_g) : '')
  const [ings, setIngs] = useState<Ingredient[]>(recipe?.ingredients ?? [])
  const [step, setStep] = useState<{ k: 'edit' } | { k: 'pick' } | { k: 'amount'; item: FoodItem }>({ k: 'edit' })
  const t = recipeTotals(ings, Number(servings) || 1, Number(cooked) || null)

  if (step.k === 'pick') {
    return (
      <div>
        <button className="linky" onClick={() => setStep({ k: 'edit' })}>‹ Back to recipe</button>
        <Search online={online} onPick={(item) => setStep({ k: 'amount', item })} onMeal={() => {}} onCreate={() => {}} />
      </div>
    )
  }
  if (step.k === 'amount') {
    return (
      <div>
        <button className="linky" onClick={() => setStep({ k: 'pick' })}>‹ Back</button>
        <FoodPortion item={step.item} actionLabel="Add ingredient" onSubmit={(p) => {
          const { source, source_ref } = refParts(step.item)
          setIngs((xs) => [...xs, { name: step.item.name, brand: step.item.brand, grams: p.grams, per100: step.item.per100, source, source_ref }])
          setStep({ k: 'edit' })
        }} />
      </div>
    )
  }

  return (
    <div>
      <label htmlFor="r-name">Recipe name</label>
      <input id="r-name" value={name} onChange={(e) => setName(e.target.value)} />
      <div className="grid2">
        <div><label htmlFor="r-serv">Servings</label>
          <input id="r-serv" inputMode="decimal" value={servings} onChange={(e) => setServings(e.target.value)} /></div>
        <div><label htmlFor="r-cook">Cooked weight (g, optional)</label>
          <input id="r-cook" inputMode="decimal" value={cooked} onChange={(e) => setCooked(e.target.value)} /></div>
      </div>
      <h2>Ingredients</h2>
      <div className="card" style={{ padding: '.2rem .4rem' }}>
        {ings.length === 0 && <p className="muted small" style={{ padding: '.4rem' }}>None yet.</p>}
        {ings.map((i, k) => (
          <div key={k} className="food-row">
            <div style={{ minWidth: 0 }}><div className="name">{i.name}</div><div className="muted small">{fmt(i.grams)} g</div></div>
            <button className="linky" onClick={() => setIngs((xs) => xs.filter((_, j) => j !== k))}>Remove</button>
          </div>
        ))}
        <button className="linky" style={{ padding: '.6rem .5rem' }} onClick={() => setStep({ k: 'pick' })}>+ Add ingredient</button>
      </div>
      <div className="card" style={{ marginTop: '1rem', padding: '.8rem 1rem' }}>
        <div className="row"><b>Per serving</b><span className="num">{fmtKcal(t.perServing.kcal ?? 0)} kcal</span></div>
        <div className="muted small">P {fmt(t.perServing.protein_g ?? 0)} · C {fmt(t.perServing.carbs_g ?? 0)} · F {fmt(t.perServing.fat_g ?? 0)} g
          {t.servingGrams ? ` · ${fmt(t.servingGrams)} g a serving` : ''}</div>
      </div>
      <button style={{ width: '100%' }} disabled={!name.trim() || ings.length === 0 || !(Number(servings) > 0)} onClick={async () => {
        await saveRecipe({ id: recipe?.id, name: name.trim(), servings: Number(servings), cooked_weight_g: Number(cooked) || null, ingredients: ings })
        onDone()
      }}>Save recipe</button>
      {recipe && <button className="secondary" style={{ width: '100%', color: 'var(--danger)', borderColor: 'var(--danger)' }}
        onClick={async () => { await deleteRecipe(recipe); onDone() }}>Delete recipe</button>}
    </div>
  )
}
